import { useCallback, useEffect, useMemo, useState } from "react";
import type { InvoiceHeader, InvoiceItem } from "../api/inventoryInvoicesApi";
import type { Application } from "../types";
import { useToast } from "../context/ToastContext";
import { buildInvoicePdfHtml, downloadInvoicePdf } from "../utils/issueInvoicePdf";

export function InvoiceGenerateSuccessTicket({
  invoice,
  items,
  applications,
  onClose,
  onCancel,
}: {
  invoice: InvoiceHeader;
  items: InvoiceItem[];
  applications?: Application[];
  onClose: () => void;
  onCancel?: () => Promise<void>;
}) {
  const { showToast } = useToast();
  const [pdfBusy, setPdfBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);

  const previewHtml = useMemo(() => {
    try {
      return buildInvoicePdfHtml(invoice, items, { applications }).html;
    } catch {
      return "";
    }
  }, [invoice, items, applications]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const runDownload = useCallback(async () => {
    setPdfBusy(true);
    try {
      await downloadInvoicePdf(invoice, items, { applications });
      showToast(`PDF downloaded (${invoice.invoiceNumber}).`, "success");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not download PDF.", "error");
    } finally {
      setPdfBusy(false);
    }
  }, [invoice, items, applications, showToast]);

  const runCancel = useCallback(async () => {
    if (!onCancel) return;
    const ok = window.confirm(`Cancel invoice ${invoice.invoiceNumber}? It will be marked inactive.`);
    if (!ok) return;
    setCancelBusy(true);
    try {
      await onCancel();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not cancel invoice.", "error");
    } finally {
      setCancelBusy(false);
    }
  }, [onCancel, invoice.invoiceNumber, showToast]);

  return (
    <div className="po-success-overlay" role="dialog" aria-modal="true" aria-labelledby="inv-success-title">
      <button type="button" className="po-success-overlay__backdrop" aria-label="Dismiss" onClick={onClose} />
      <div className="po-success-ticket">
        <header className="po-success-ticket__header">
          <h2 id="inv-success-title">Invoice generated</h2>
          <button type="button" className="po-success-ticket__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="po-success-ticket__meta">
          <p>
            <strong>{invoice.invoiceNumber}</strong> · {invoice.partyFirmName} · {invoice.beneficiaryCount}{" "}
            beneficiary(ies)
          </p>
          <p className="text-muted">
            Taxable ₹{invoice.taxableTotal.toLocaleString("en-IN")} · Grand total ₹
            {invoice.grandTotal.toLocaleString("en-IN")}
          </p>
        </div>
        {previewHtml ? (
          <iframe
            className="po-success-ticket__preview"
            title="Invoice preview"
            srcDoc={previewHtml}
            sandbox="allow-same-origin"
          />
        ) : null}
        <div className="po-success-ticket__actions">
          <button type="button" className="btn btn--primary btn--tg" disabled={pdfBusy} onClick={() => void runDownload()}>
            {pdfBusy ? "Preparing PDF…" : "Download PDF"}
          </button>
          {onCancel ? (
            <button
              type="button"
              className="btn btn--danger btn--tg-secondary"
              disabled={cancelBusy}
              onClick={() => void runCancel()}
            >
              {cancelBusy ? "Cancelling…" : "Cancel invoice"}
            </button>
          ) : null}
          <button type="button" className="btn btn--tg-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
